import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import axios from "axios";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import CallDetailsTabs from "../../calldetails/components/CallDetailsTabs";
import { updateNewLeadTypeAsync } from "../redux";

const NewDataCallDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { cdnURL, adminBaseURL } = useSelector(
    (state) => state.environnment.environmentLists,
    shallowEqual
  );

  const [isLoading, setIsLoading] = useState(true);
  const [coldLead, setColdLead] = useState({});
  const [callItems, setCallItems] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [leadType, setLeadType] = useState("Unblock"); // "Blocked" or "Unblock"
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    const canClose = window.opener || window.history.length <= 1;
    if (canClose) {
      window.close();
    } else {
      window.location.assign("/new-data-call");
    }
  };

  const fetchCallDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await axios({
        method: "GET",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/newLeadsCalling/getCallDetails/${id}`,
        headers: { "Content-Type": "application/json" },
      });
      // Handle both API response formats: status (from API docs) and code (legacy)
      if (data && (data.status === 200 || data.code === 200) && data.data) {
        const payload = data.data || {};
        // Lead may use ClientName, Email, ContactNumber (new data call) or legacy name/email/contactNumber
        // Keep fallback for legacy formats
        const leadData = payload.lead || payload.coldLead || payload.cold_lead || payload;
        setColdLead(leadData);
        // Set lead type from lead data (leadType: "Blocked" | "Unblock" | null)
        if (leadData.leadType === "Blocked" || leadData.leadType === "Unblock") {
          setLeadType(leadData.leadType);
        } else {
          setLeadType("Unblock"); // Default to Unblock if not set or null
        }
        const items = payload.call_ai || payload.callAi || payload.callRecords || [];
        setCallItems(Array.isArray(items) ? items : []);
      } else {
        setColdLead({});
        setCallItems([]);
        setLeadType("Unblock");
      }
    } catch (error) {
      setColdLead({});
      setCallItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [id, adminBaseURL]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await fetchCallDetails();
      if (!mounted) return;
    })();
    return () => {
      mounted = false;
    };
  }, [fetchCallDetails]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, []);

  const getFormattedTime = () => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return currentTime.toLocaleTimeString('en-US', {
        timeZone: timeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch (e) {
      return currentTime.toLocaleTimeString();
    }
  };

  const handleLeadTypeChange = (type) => {
    setLeadType(type);
  };

  const handleSaveLeadType = async () => {
    setIsSaving(true);
    try {
      // API expects "Blocked", "Unblock", or null
      const leadTypeValue = leadType === "Blocked" ? "Blocked" : "Unblock";

      const result = await dispatch(updateNewLeadTypeAsync(id, leadTypeValue));

      if (result && result.success) {
        // Refresh lead data to get updated values
        await fetchCallDetails();
      }
    } catch (error) {
      console.error("Error updating lead type:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ background: "#f3f4f6", height: "calc(100vh - 160px)", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <div
          style={{
            background: "#ffffff",
            borderRadius: 16,
            boxShadow: "0 15px 40px rgba(15, 23, 42, 0.08)",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              padding: "20px 28px 12px",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, position: "relative" }}>
              <div style={{ fontSize: 20, fontWeight: 600 }}>AI Agent Call Review</div>
              <button
                type="button"
                className="btn"
                onClick={handleClose}
                style={{
                  backgroundColor: "#fff",
                  color: "#991b1b",
                  borderRadius: 999,
                  boxShadow: "none",
                  fontWeight: 600,
                  minWidth: "auto",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 30,
                  width: 30,
                  padding: 0,
                  position: "absolute",
                  top: -17,
                  right: -25,
                }}
              >
                <span style={{ color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <SVG src={toAbsoluteUrl("/media/svg/icons/custom/Close.svg")} height={30} width={30} />
                </span>
              </button>
            </div>

            {/* Lead Type Component */}
            <div style={{ marginTop: 12, marginBottom: 12, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#4b5563" }}>
                Lead Type
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: 6,
                  }}
                >
                  <input
                    type="radio"
                    name="leadType"
                    value="Unblock"
                    checked={leadType === "Unblock"}
                    onChange={() => handleLeadTypeChange("Unblock")}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: "pointer",
                      accentColor: "#10b981",
                    }}
                  />
                  <span
                    style={{
                      color: leadType === "Unblock" ? "#10b981" : "#6b7280",
                      fontWeight: leadType === "Unblock" ? 600 : 400,
                      fontSize: 14,
                    }}
                  >
                    Unblock
                  </span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    gap: 6,
                  }}
                >
                  <input
                    type="radio"
                    name="leadType"
                    value="Blocked"
                    checked={leadType === "Blocked"}
                    onChange={() => handleLeadTypeChange("Blocked")}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: "pointer",
                      accentColor: "#ef4444",
                    }}
                  />
                  <span
                    style={{
                      color: leadType === "Blocked" ? "#ef4444" : "#6b7280",
                      fontWeight: leadType === "Blocked" ? 600 : 400,
                      fontSize: 14,
                    }}
                  >
                    Blocked
                  </span>
                </label>
              </div>
              {/* <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 13, color: "#6b7280" }}>Selected: </span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: leadType === "Unblock" ? "#10b981" : "#ef4444",
                  }}
                >
                  {leadType}
                </span>
              </div> */}
              <button
                type="button"
                onClick={handleSaveLeadType}
                disabled={isSaving}
                style={{
                  backgroundColor: "#7c3aed",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 20px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: isSaving ? "not-allowed" : "pointer",
                  opacity: isSaving ? 0.6 : 1,
                  transition: "opacity 0.2s",
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Lead Name</div>
                <div style={{ fontWeight: 600, color: "#111827" }}>
                  {coldLead?.ClientName || coldLead?.name || "-"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Email</div>
                <div style={{ fontWeight: 600, color: "#111827" }}>
                  {coldLead?.Email || coldLead?.email || "-"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Mobile</div>
                <div style={{ fontWeight: 600, color: "#111827" }}>
                  {coldLead?.ContactNumber || coldLead?.contactNumber || "-"}
                </div>
              </div>
              {coldLead?.countryName && (
                <div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Country</div>
                  <div style={{ fontWeight: 600, color: "#111827" }}>{coldLead?.countryName}</div>
                </div>
              )}
              {coldLead?.currentCountryTime && (
                <div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>Current Time</div>
                  <div style={{ fontWeight: 600, color: "#111827" }}>{coldLead?.currentCountryTime}</div>
                </div>
              )}
            </div>
          </div>

          <div style={{ padding: 24, background: "#f8fafc", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <div
              style={{
                background: "#ffffff",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              <CallDetailsTabs
                callItems={callItems}
                cdnURL={cdnURL}
                emptyMessage="No AI call records available for this new data call."
                onRefresh={fetchCallDetails}
                leadId={id}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDataCallDetails;


